import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { BarChart3, RefreshCw, Eye, Lock, MousePointerClick, BookOpen, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
    { id: "24h", label: "24h" },
    { id: "7d", label: "7d" },
    { id: "30d", label: "30d" },
    { id: "all", label: "All" },
];

const FUNNEL = [
    { key: "topic_views", label: "Topic views", Icon: Eye },
    { key: "gate_views", label: "Gate views", Icon: Lock },
    { key: "gate_cta_clicks", label: "CTA clicks", Icon: MousePointerClick },
    { key: "source_reader_opens", label: "Source reader opens", Icon: BookOpen },
    { key: "magic_link_requests", label: "Magic link requests", Icon: Mail },
    { key: "magic_link_verifies", label: "Magic link verifies", Icon: ShieldCheck },
];

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [range, setRange] = useState("7d");
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/admin/analytics?range=${range}`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load analytics"))
            .finally(() => setLoading(false));
    }, [range]);

    useEffect(() => { load(); }, [load]);

    const f = data?.funnel || {};
    const aud = data?.audience || {};

    // Compute conversion rates between adjacent funnel steps (cinematic — single ratio only).
    function rate(a, b) {
        if (!a || a === 0) return null;
        return Math.round((b / a) * 100);
    }
    const ctr = rate(f.gate_views, f.gate_cta_clicks);
    const cta_to_link = rate(f.gate_cta_clicks, f.magic_link_requests);
    const link_verify = rate(f.magic_link_requests, f.magic_link_verifies);

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-6xl">
                <header className="mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-3">
                        <BarChart3 className="w-3.5 h-3.5" />
                        Operations · Internal analytics
                    </div>
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white tracking-tight">Internal analytics</h1>
                            <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
                                Real-time funnel and content engagement, complementing GA4 + Clarity. Counts are exact and refreshed live; no 24-hour aggregation lag.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="inline-flex bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid="analytics-range">
                                {RANGES.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => setRange(r.id)}
                                        data-testid={`analytics-range-${r.id}`}
                                        className={`px-3 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${range === r.id ? "bg-[#2F80ED] text-white" : "text-[#94A3B8] hover:text-white"}`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                data-testid="analytics-refresh"
                                onClick={load}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] text-[#94A3B8] hover:text-white border border-[#243044] hover:border-[#2F80ED]"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </header>

                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3">Trial funnel</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-funnel">
                        {FUNNEL.map(({ key, label, Icon }) => (
                            <div key={key} className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                                    <Icon className="w-3.5 h-3.5" />
                                    {label}
                                </div>
                                <div className="text-2xl font-bold text-white tracking-tight">{f[key] ?? 0}</div>
                            </div>
                        ))}
                    </div>
                    {(ctr !== null || cta_to_link !== null || link_verify !== null) && (
                        <div className="mt-4 text-xs text-[#94A3B8] flex flex-wrap gap-6 items-center">
                            {ctr !== null && <span>Gate → CTA: <span className="text-[#56CCF2] font-mono">{ctr}%</span></span>}
                            {cta_to_link !== null && <span>CTA → Magic link: <span className="text-[#56CCF2] font-mono">{cta_to_link}%</span></span>}
                            {link_verify !== null && <span>Link sent → verified: <span className="text-[#56CCF2] font-mono">{link_verify}%</span></span>}
                        </div>
                    )}
                </section>

                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3">Audience split</h2>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-6 flex items-center gap-8 flex-wrap" data-testid="analytics-audience">
                        <Split label="Members" value={aud.member || 0} dot="#56CCF2" />
                        <Split label="Public" value={aud.public || 0} dot="#94A3B8" />
                        <Split label="Preview" value={aud.preview || 0} dot="#F4B731" />
                    </div>
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Panel title="Top investigations" rows={data?.top_topics} testId="analytics-top-topics" labelKey="slug" />
                    <Panel title="Top sources opened" rows={data?.top_sources} testId="analytics-top-sources" labelKey="key" />
                    <Panel title="Top searches" rows={data?.top_searches} testId="analytics-top-searches" labelKey="term" />
                </div>
            </div>
        </AdminLayout>
    );
}

function Split({ label, value, dot }) {
    return (
        <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
            <span className="text-xs uppercase tracking-[0.18em] text-[#64748B]">{label}</span>
            <span className="text-xl font-bold text-white tracking-tight">{value}</span>
        </div>
    );
}

function Panel({ title, rows, testId, labelKey }) {
    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid={testId}>
            <div className="px-6 py-3 border-b border-[#243044] text-xs font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">{title}</div>
            {!rows || rows.length === 0 ? (
                <div className="px-6 py-8 text-center text-xs text-[#64748B] italic">No events in this window.</div>
            ) : (
                <ul className="divide-y divide-[#243044]">
                    {rows.map((r, i) => (
                        <li key={`${r[labelKey]}-${i}`} className="px-6 py-3 flex items-center justify-between text-sm">
                            <span className="font-mono text-white truncate pr-3" title={r[labelKey]}>{r[labelKey]}</span>
                            <span className="text-[#56CCF2] font-mono text-xs">{r.count}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
